import { EnvVarPopover } from "@/components/EnvVarPopover";
import { Button } from "@/components/ui/button";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuItem,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ResizableHandle, ResizablePanel } from "@/components/ui/resizable";
import {
  FpTabs,
  FpTabsContent,
  FpTabsList,
  FpTabsTrigger,
} from "@/components/ui/tabs";
import { BRAINSTORM_ID } from "@/constants";
import { useHandler } from "@/hooks";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { useActiveSnapshotId } from "@/lib/hooks/useActiveSnapshot";
import { useFilesystemStore } from "@/state/stores";
import {
  ActivityIcon,
  BrainIcon,
  ChevronDownIcon,
  CloudIcon,
  CodeIcon,
  CopyIcon,
  ExternalLinkIcon,
  EyeIcon,
  GlobeIcon,
  ShapesIcon,
} from "lucide-react";
import { FilesystemView } from "../../SnapshotId/FilesystemView";
import { PreviewView } from "../PreviewView";
import { useFileOperations } from "../hooks/useFileOperations";
import { useLatestDeploymentUrl } from "../hooks/useLatestDeploymentUrl";
import { useTabNavigation } from "../hooks/useTabNavigation";
import { SpecViewer } from "./SpecViewer";
import { VersionDropdown } from "./VersionDropdown";
import { TraceViewer } from "./traces";

export function FilesystemSection() {
  const projectId = useActiveProjectId();
  const snapshotId = useActiveSnapshotId();

  // TODO - Should this be for length > 1?
  // TODO - Need to handle deduplicating snapshots + current
  const hasMultipleSnapshots = useFilesystemStore(
    (state) => state.snapshots.length > 0,
  );

  const tabNavigation = useTabNavigation({
    projectId: projectId ?? "",
    snapshotId,
  });
  const { computedActiveTab, navigateTab } = tabNavigation;

  const { downloadFiles } = useFileOperations();
  const handleDownloadFiles = useHandler(() => {
    downloadFiles(projectId);
  });

  const latestDeployUrl = useLatestDeploymentUrl(projectId);

  const handleCopyUrl = useHandler(async () => {
    if (latestDeployUrl) {
      try {
        await navigator.clipboard.writeText(latestDeployUrl);
      } catch (error) {
        console.error("Failed to copy URL:", error);
      }
    }
  });

  return (
    <>
      <ResizableHandle />
      <ResizablePanel id="attachments" defaultSize={50}>
        <FpTabs
          className="h-full flex flex-col bg-background"
          value={computedActiveTab}
          onValueChange={navigateTab}
        >
          <header className="border-b shrink-0">
            <FpTabsList className="flex items-center justify-between h-12 px-4">
              <div className="flex gap-1">
                <FpTabsTrigger value="brainstorm">
                  <BrainIcon className="w-4 h-4" /> Plan
                </FpTabsTrigger>
                <FpTabsTrigger value="code">
                  <CodeIcon className="w-4 h-4" /> Code
                </FpTabsTrigger>
                <FpTabsTrigger value="preview">
                  <EyeIcon className="w-4 h-4" /> Preview
                </FpTabsTrigger>
                <FpTabsTrigger value="trace">
                  <ActivityIcon className="w-4 h-4" /> Traces
                </FpTabsTrigger>
              </div>
              <div className="flex items-center gap-2">
                {hasMultipleSnapshots && <VersionDropdown />}

                {projectId && <EnvVarPopover projectId={projectId} />}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleDownloadFiles}
                >
                  <ShapesIcon />
                  Download
                </Button>

                <FpDropdownMenu>
                  <FpDropdownMenuTrigger asChild>
                    <Button variant="primary" size="sm">
                      <CloudIcon />
                      Deploy
                      <ChevronDownIcon className="w-3 h-3" />
                    </Button>
                  </FpDropdownMenuTrigger>
                  <FpDropdownMenuContent align="end">
                    {latestDeployUrl ? (
                      <>
                        <FpDropdownMenuItem className="hover:bg-muted focus:bg-muted cursor-pointer">
                          <a
                            href={latestDeployUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 justify-between w-full"
                          >
                            <GlobeIcon className="w-4 h-4" />
                            <span className="text-xs">{latestDeployUrl}</span>
                            <ExternalLinkIcon className="w-4 h-4" />
                          </a>
                        </FpDropdownMenuItem>
                        <FpDropdownMenuItem
                          onClick={handleCopyUrl}
                          className="hover:bg-muted focus:bg-muted cursor-pointer"
                        >
                          <CopyIcon className="w-4 h-4" />
                          Copy URL
                        </FpDropdownMenuItem>
                      </>
                    ) : (
                      <FpDropdownMenuItem disabled>
                        <CloudIcon className="w-4 h-4" />
                        No deployments yet
                      </FpDropdownMenuItem>
                    )}
                  </FpDropdownMenuContent>
                </FpDropdownMenu>
              </div>
            </FpTabsList>
          </header>
          <FpTabsContent
            id={BRAINSTORM_ID}
            value="brainstorm"
            // The ToC relies on this relative positioning as it can't be part
            // of the content scroll area
            className="flex-1 overflow-hidden relative"
          >
            <SpecViewer />
          </FpTabsContent>
          <FpTabsContent value="code" className="flex-1 overflow-hidden">
            <div className="h-full flex flex-col overflow-y-auto bg-background">
              <FilesystemView />
            </div>
          </FpTabsContent>
          <FpTabsContent value="preview" className="flex-1 overflow-hidden">
            <PreviewView />
          </FpTabsContent>
          <FpTabsContent value="trace" className="flex-1 overflow-hidden">
            <TraceViewer />
          </FpTabsContent>
        </FpTabs>
      </ResizablePanel>
    </>
  );
}
